import { AlertTriangle } from "lucide-react";
import { formatDuration } from "utils/planPreview";

const every = (seconds) => (seconds < 90 ? `${Math.round(seconds)} s` : formatDuration(seconds));
const endTime = (seconds) =>
  new Date(Date.now() + seconds * 1000).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" });

function Share({ label, seconds, total, detail }) {
  const pct = total > 0 ? Math.round((seconds / total) * 100) : 0;
  return (
    <li className="flex flex-col gap-0.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-slate-700">{label}</span>
        <span className="text-slate-500">{formatDuration(seconds)}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100" aria-hidden="true">
        <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
      </div>
      {detail && <span className="text-[11px] text-slate-500">{detail}</span>}
    </li>
  );
}

/**
 * What a person can expect if they start this campaign now: the total and the end time, where the time goes, what
 * makes it long (a slow sensor, the pump's daily limit) and what happens step by step. All numbers come from the
 * backend's estimate (POST /experiment/estimate/).
 */
export default function CampaignExpectation({ estimate, conditionsCount, levelFirst, sensorIntervalS, pumpMinutesPerDay = 450, firstError }) {
  if (!estimate) {
    return (
      <div className="rounded-xl border border-slate-200 p-3">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">What to expect</span>
        <p className="mt-2 text-sm text-slate-400">{firstError || "Working it out…"}</p>
      </div>
    );
  }
  const visits = estimate.visits || [];
  const settleS = visits.reduce((sum, v) => sum + (v.soak_s || 0), 0);
  const settleCount = visits.filter((v) => v.soak_s).length;
  const levelCount = visits.reduce((sum, v) => sum + (v.levels_cm?.length || 0), 0);
  const waitS = levelCount * (estimate.hold_s || 0);
  const moveS = Math.max(0, visits.reduce((sum, v) => sum + (v.staircase_s || 0), 0) - waitS);
  const typicalSettle = visits.find((v) => v.soak_s)?.soak_s;
  const slowSensor = estimate.requested_hold_s && estimate.hold_s > estimate.requested_hold_s + 1;
  const extraS = estimate.seconds - (estimate.seconds_at_requested_hold ?? estimate.seconds);
  const pauses = estimate.overnight_pauses || 0;
  const first = visits[0];
  const perCondition = first ? (first.soak_s || 0) + (first.staircase_s || 0) : 0;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 p-3" data-testid="campaign-expectation">
      <span className="text-xs font-bold uppercase tracking-wide text-slate-500">What to expect</span>
      <div data-testid="campaign-estimate">
        <p className="text-lg font-bold text-slate-900">About {formatDuration(estimate.seconds)}</p>
        <p className="text-xs text-slate-600">
          {pauses
            ? `Plus ${pauses === 1 ? "an overnight pause" : `${pauses} overnight pauses`} for the pump, so it ends later than ${endTime(estimate.seconds)}.`
            : `If you start now, it ends around ${endTime(estimate.seconds)}.`}
          {!levelFirst && perCondition ? ` Each condition takes about ${formatDuration(perCondition)}.` : ""}
        </p>
      </div>

      {slowSensor && (
        <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="campaign-slow-sensor">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>The sensor is slow.</strong> It reports every {every(sensorIntervalS || 0)}, and each level needs{" "}
            {estimate.readings_per_level || 3} readings, so each level waits {formatDuration(estimate.hold_s)} instead of the{" "}
            {formatDuration(estimate.requested_hold_s)} you set. That adds about {formatDuration(extraS)}. With the sensor reporting
            every few seconds this plan takes about {formatDuration(estimate.seconds_at_requested_hold)}.
          </span>
        </div>
      )}
      {pauses > 0 && (
        <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="campaign-overnight">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            The pump may only run {formatDuration(pumpMinutesPerDay * 60)} a day and this plan needs {formatDuration(estimate.pump_s)}, so it
            pauses overnight {pauses === 1 ? "once" : `${pauses} times`} with the chamber stopped, then carries on by itself.
          </span>
        </div>
      )}

      <div>
        <p className="text-xs font-semibold text-slate-700">Where the time goes</p>
        <ul className="mt-1 flex flex-col gap-2">
          <Share label="Waiting at each level" seconds={waitS} total={estimate.seconds}
            detail={`${levelCount} levels × ${formatDuration(estimate.hold_s)}`} />
          {settleS > 0 && (
            <Share label="Settling after the chamber changes" seconds={settleS} total={estimate.seconds}
              detail={`${settleCount} change${settleCount === 1 ? "" : "s"} × about ${formatDuration(typicalSettle)}`} />
          )}
          <Share label="Pumping and photos" seconds={moveS} total={estimate.seconds}
            detail={`the pump runs ${formatDuration(estimate.pump_s)} in all`} />
        </ul>
      </div>

      <div>
        <p className="text-xs font-semibold text-slate-700">When you press Start</p>
        <ol className="mt-1 list-decimal pl-5 text-xs text-slate-600">
          <li>
            The chamber is set to the first condition
            {settleS > 0 ? ` and settles (about ${formatDuration(typicalSettle)})` : " and the water starts moving straight away; the chamber settles meanwhile"}.
          </li>
          <li>
            {levelFirst
              ? "The water stays at its level while the chamber goes through every condition, then moves to the next level."
              : "The water steps through each level, waiting at each one while the camera photographs the ruler."}
          </li>
          <li>This repeats until all {conditionsCount} conditions are done. You can watch it on this page.</li>
          <li>At the end the chamber goes back to 25 °C / 60 %.</li>
        </ol>
        <p className="mt-1 text-[11px] text-slate-500">
          Every reading is saved with the temperature and humidity the sensor really measured, so it doesn't matter if the
          chamber is still on its way to the target. You can pause or stop at any time; if something looks wrong (the water
          too high, a pump problem) it stops the chamber and waits for you.
        </p>
      </div>
    </div>
  );
}
